import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import date from 'date-and-time';
import { faListMusic } from '@fortawesome/pro-light-svg-icons/faListMusic';
import { TypedMemo } from '../../../common/types';
import { usePlaylistsTopByFollowers } from '../../hooks/usePlaylistsTopByFollowers';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { useSMLPaginationParam } from '../../hooks/useSMLPaginationParam';
import { SMLResultsCard } from '../SMLResultsCard/SMLResultsCard';
import { SMLResultsItem } from '../SMLResultsCard/props';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { datePickerParamName } from '../SMLDatePicker/consts';
import { IntegrationTypeIcon } from '../IntegrationTypeIcon';

const paginationParamName = 'topFollowersPage';

const formatFollowersCount = (count: number): string => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(0)}K`;
  }
  return count.toString();
};

const TopPlaylistsByFollowersCardComponent: React.FC = () => {
  const intl = useIntl();
  const { selectedDate, selectedDateValue } = useSMLDatePickerDateParam();
  const { currentPage } = useSMLPaginationParam(paginationParamName);
  const {
    data: playlistsData,
    isLoading,
    error,
    refetch,
  } = usePlaylistsTopByFollowers({
    page: currentPage,
    date: selectedDate,
  });

  const playlists = useMemo(() => playlistsData?.playlists ?? [], [playlistsData?.playlists]);

  const items: SMLResultsItem[] = useMemo(
    () =>
      playlists.map((playlist) => ({
        id: playlist.playlistId,
        name: playlist.name,
        position: playlist.position,
        imageUrl: playlist.imageUrl,
        trackImageUrls: playlist.trackImageUrls,
        rightElement: (
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <IntegrationTypeIcon integrationType={playlist.integrationType} size={20} />
            <span className="text-muted">{formatFollowersCount(playlist.followersCount)}</span>
          </div>
        ),
        chart: { entityType: 'playlist' as const, date: selectedDate },
        route: SMLRoutes.playlistDetails,
        params: { playlistId: playlist.playlistId, [datePickerParamName]: selectedDateValue },
      })),
    [playlists, selectedDate, selectedDateValue]
  );

  const title = intl.formatMessage({
    id: 'app.sml.playlists.topByFollowers.title',
    defaultMessage: 'Top by Followers',
  });

  const subtitle = date.format(selectedDate, 'MMM DD, YYYY');

  return (
    <SMLResultsCard
      title={title}
      subtitle={subtitle}
      icon={faListMusic}
      iconColor="var(--brand-violet-lift)"
      items={items}
      isLoading={isLoading}
      error={error}
      onRetry={() => {
        refetch().catch(console.error);
      }}
      imageShape="rounded-square"
      showCountryFilter
      paginationParamName={paginationParamName}
    />
  );
};

export const TopPlaylistsByFollowersCard = TypedMemo(TopPlaylistsByFollowersCardComponent);
